import { Component, HostListener } from '@angular/core';
import { GridApi } from 'ag-grid-community';
import { NgxSpinnerService } from 'ngx-spinner';
import { ActionsComponent } from 'src/app/cell-renderer/actions/actions.component';
import { LoadingGridComponent } from 'src/app/cell-renderer/loading-grid/loading-grid.component';
import { UnitService } from 'src/app/services/unit.service';
import { UtilsService } from 'src/app/services/utils.service';
import { HttpEventType } from '@angular/common/http';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-unit-list',
    templateUrl: './unit-list.component.html',
    styleUrls: ['./unit-list.component.css']
})
export class UnitListComponent {
    constructor(private apiUnit: UnitService, private utils: UtilsService, private spinner: NgxSpinnerService, private router: Router) { }

    private importing = false;
    public actionsOpen = false;

    filterUnit = '';
    filterPlates = '';
    filterDisponibilidad = '';

    get unitOptions(): string[] {
        const units = (this.rowData as any[]).map(r => r.unit).filter(Boolean);
        return [...new Set(units)].sort() as string[];
    }

    get disponibilidadOptions(): string[] {
        const statuses = (this.rowData as any[]).map(r => r.status?.status).filter(Boolean);
        return [...new Set(statuses)] as string[];
    }

    onFilterChange(): void {
        if (this.gridApi) {
            this.gridApi.onFilterChanged();
        }
    }

    isExternalFilterPresent = (): boolean => {
        return !!(this.filterUnit || this.filterPlates || this.filterDisponibilidad);
    };

    doesExternalFilterPass = (node: any): boolean => {
        const row = node.data;
        const matchUnit = !this.filterUnit || row.unit === this.filterUnit;
        const matchPlates = !this.filterPlates ||
            (row.plates || '').toLowerCase().includes(this.filterPlates.toLowerCase());
        const matchDisp = !this.filterDisponibilidad || row.status?.status === this.filterDisponibilidad;
        return matchUnit && matchPlates && matchDisp;
    };

    columnDefs = [
        {
            field: 'unit',
            headerName: 'Unidad',
            minWidth: 180,
            cellStyle: () => ({ color: '#00a0e1', textDecoration: 'underline', cursor: 'pointer' }) as any,
            onCellClicked: (params: any) => this.router.navigate(['/unit/maintenance-history', params.data?.id])
        },
        { field: 'plates', headerName: 'Placas', minWidth: 120 },
        {
            field: 'status.status',
            headerName: 'Disponibilidad',
            headerClass: 'text-center',
            minWidth: 160,
            cellStyle: (params: any) => {
                const color = params?.data?.status?.bg_color;
                const textColor = params?.data?.status?.text_color;
                return { backgroundColor: color, color: textColor, display: 'flex', alignItems: 'center', fontWeight: '500', justifyContent: 'center' } as any;
            }
        },
        {
            field: 'latest_maintenance_request.estimated_time_repair_start',
            headerName: 'Fecha ingreso mantenimiento',
            cellClass: "textEllipsis",
            minWidth: 190,
            cellRenderer: (params: any) => {
                const date = params?.value;
                return date ? this.utils.formatCreatedAt(date, 2) : '';
            },
            cellStyle: () => ({ display: 'flex', alignItems: 'center', justifyContent: 'center' }) as any
        },
        {
            field: 'latest_maintenance_request.estimated_time_repair_end',
            headerName: 'Fecha entrega de unidad',
            minWidth: 170,
            cellRenderer: (params: any) => {
                const date = params?.value;
                return date ? this.utils.formatCreatedAt(date, 2) : '';
            },
            cellStyle: () => ({ display: 'flex', alignItems: 'center', justifyContent: 'center' }) as any
        },
        {
            field: 'checklist_status',
            headerName: 'Checklist',
            headerClass: 'text-center',
            minWidth: 110,
            valueFormatter: (params: any) => params.value || 'Pendiente',
            cellStyle: (params: any) => {
                const status = params.value || 'Pendiente';
                const isRealizado = status === 'Realizado';
                const bg = isRealizado ? '#e3fbe8' : '#fce8e8';
                const color = isRealizado ? '#3c7e43' : '#c0392b';
                return { background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.78rem', fontWeight: '500', padding: '3px 12px', } as any;
            }
        },
        {
            headerName: 'Registro de consumo',
            sortable: false,
            filter: false,
            width: 220,
            flex: 0,
            suppressSizeToFit: true,
            headerClass: 'text-center',
            cellRenderer: (params: any) => {
                const fuelDays: number[] = params.data?.fuel_days ?? [];
                const days = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
                const circles = days.map((d, i) => {
                    const hasRecord = fuelDays.includes(i + 1);
                    const bg = hasRecord ? '#E3FBE8' : '#E2E1E3';
                    const color = hasRecord ? '#3c7e43' : '#6c757d';
                    return `<span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;background:${bg};color:${color};font-size:0.68rem;font-weight:700;">${d}</span>`;
                }).join('');
                return `<div style="display:flex;align-items:center;gap:3px;justify-content:center;">${circles}</div>`;
            },
            cellStyle: () => ({ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }) as any,
            onCellClicked: (params: any) => this.router.navigateByUrl('/unit/fuel-consumption', { state: { data: params.data } })
        },
        {
            headerName: 'Acciones', 
            width: 220, 
            cellRenderer: ActionsComponent,
            suppressSizeToFit: true,
            sortable: false,
            filter: false,
            flex:0,
            headerClass: 'text-center',
            cellRendererParams: {
                parent: this, url_edit: '/unit/update/',
                url_checklist: '/unit/checklist/',
                module: 'Unidades',
                singular_module: 'Unidad'
            },
            cellStyle: () => ({ display: 'flex', alignItems: 'center', justifyContent: 'center' }) as any
        },
    ];

    public loadingCellRenderer: any = LoadingGridComponent;
    public loadingCellRendererParams: any = {
        loadingMessage: 'Espere un momento...',
    };

    public defaultColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        flex: 1,
    };

    private gridApi!: GridApi;
    public rowData: any = [];

    ngOnInit(): void { }

    onGridReady(params: any) {
        this.gridApi = params.api;
        this.spinner.show();
        this.apiUnit.listUnit().subscribe((resp) => {
            this.rowData = resp;
            this.spinner.hide();
        });
    }

    exportPermitTypesCsv() {
        this.spinner.show();
        this.apiUnit.getPermitTypes().subscribe({
            next: (resp: any) => {
                this.spinner.hide();
                const items: Array<{ id?: number; key?: string; description?: string; transport_key?: string }> = Array.isArray(resp) ? resp : [];
                const headers = ['id', 'key', 'description', 'transport_key'];
                const rows = items.map((i) => [i?.id ?? '', i?.key ?? '', i?.description ?? '', i?.transport_key ?? '']);
                const blob = this.createCsvBlob(headers, rows, ';');
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `permit-types-export-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo exportar los tipos de permiso', icon: 'error' });
            }
        });
    }

    exportVehicleConfigurationsCsv() {
        this.spinner.show();
        this.apiUnit.getVehicleConfigurations().subscribe({
            next: (resp: any) => {
                this.spinner.hide();
                const items: Array<{ id?: number; code?: string; description?: string }> = Array.isArray(resp) ? resp : [];
                const headers = ['id', 'code', 'description'];
                const rows = items.map((i) => [i?.id ?? '', i?.code ?? '', i?.description ?? '']);
                const blob = this.createCsvBlob(headers, rows, ';');
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `vehicle-configurations-export-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo exportar la configuración vehicular', icon: 'error' });
            }
        });
    }

    exportInsurersCsv() {
        this.spinner.show();
        this.apiUnit.getInsurers().subscribe({
            next: (resp: any) => {
                this.spinner.hide();
                const insurers: Array<{ id?: number; name?: string }> = Array.isArray(resp) ? resp : [];
                const headers = ['id', 'name'];
                const rows = insurers.map((i) => [i?.id ?? '', i?.name ?? '']);
                const blob = this.createCsvBlob(headers, rows, ';');
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `insurers-export-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo exportar las aseguradoras', icon: 'error' });
            }
        });
    }

    private createCsvBlob(headers: string[], rows: any[][], delimiter: string) {
        const escape = (value: any) => {
            const s = value === null || value === undefined ? '' : String(value);
            const needsQuotes = s.includes('"') || s.includes('\n') || s.includes('\r') || s.includes(delimiter);
            const escaped = s.replace(/"/g, '""');
            return needsQuotes ? `"${escaped}"` : escaped;
        };
        const headerLine = headers.map(escape).join(delimiter);
        const bodyLines = rows.map((r) => r.map(escape).join(delimiter));
        const content = [headerLine, ...bodyLines].join('\n');
        return new Blob([content], { type: 'text/csv;charset=utf-8' });
    }

    toggleActionsDropdown() {
        this.actionsOpen = !this.actionsOpen;
    }

    closeActionsDropdown() {
        this.actionsOpen = false;
    }

    @HostListener('document:click')
    onDocumentClick() {
        this.closeActionsDropdown();
    }

    exportCsv() {
        this.spinner.show();
        this.apiUnit.exportUnits().subscribe({
            next: (blob: any) => {
                this.spinner.hide();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `units-export-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo exportar el CSV', icon: 'error' });
            }
        });
    }

    openCsvPicker(input: HTMLInputElement) {
        if (this.importing) return;
        this.closeActionsDropdown();
        input.value = '';
        input.click();
    }

    onCsvSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input?.files?.[0];
        if (!file) return;
        this.importCsv(file);
    }

    private importCsv(file: File) {
        this.importing = true;
        Swal.fire({
            title: 'Importando unidades',
            html: `
                <div style="text-align:left">
                  <div style="margin-bottom:8px">Subiendo archivo y procesando…</div>
                  <div style="width:100%;background:#e9ecef;border-radius:6px;overflow:hidden;height:12px">
                    <div id="importProgressBar" style="width:0%;height:12px;background:#0d6efd"></div>
                  </div>
                  <div id="importProgressText" style="margin-top:8px;font-size:12px;color:#6c757d">0%</div>
                </div>
            `,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => { Swal.showLoading(); }
        });

        this.apiUnit.importUnits(file).subscribe({
            next: (event) => {
                if (event.type === HttpEventType.UploadProgress) {
                    const total = event.total ?? 0;
                    const loaded = event.loaded ?? 0;
                    const percent = total > 0 ? Math.round((loaded / total) * 100) : 0;
                    const bar = document.getElementById('importProgressBar');
                    const txt = document.getElementById('importProgressText');
                    if (bar) bar.style.width = `${percent}%`;
                    if (txt) txt.textContent = `${percent}%`;
                }
                if (event.type === HttpEventType.Response) {
                    const resp: any = event.body;
                    Swal.close();
                    const summary = resp?.summary;
                    const errors = resp?.errors ?? [];
                    const inserted = summary?.inserted ?? 0;
                    const updated = summary?.updated ?? 0;
                    const failed = summary?.failed ?? 0;
                    const html = `
                      <div style="text-align:left">
                        <div><strong>Insertados:</strong> ${inserted}</div>
                        <div><strong>Actualizados:</strong> ${updated}</div>
                        <div><strong>Fallidos:</strong> ${failed}</div>
                      </div>
                    `;
                    Swal.fire({
                        title: 'Importación finalizada',
                        html,
                        icon: failed > 0 ? 'warning' : 'success',
                        showCancelButton: failed > 0,
                        confirmButtonText: 'Aceptar',
                        cancelButtonText: failed > 0 ? 'Descargar errores' : undefined,
                    }).then((result) => {
                        if (result.dismiss === Swal.DismissReason.cancel && failed > 0) {
                            this.downloadJson(errors, 'unit-import-errors.json');
                        }
                    });
                    this.onGridReady({ api: this.gridApi });
                    this.importing = false;
                }
            },
            error: (error) => {
                Swal.close();
                this.importing = false;
                this.utils.alertSwal({ title: 'Error', text: String(error || 'Error al importar'), icon: 'error' });
            }
        });
    }

    private downloadJson(data: any, filename: string) {
        try {
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) { }
    }

    actionDelete(id: number) {
        this.utils.alertSwalConfir({ title: 'Eliminación', text: 'Esta seguro que desea eliminar la unidad seleccionada', icon: 'warning', btn_confirm: 'Eliminar' })
            .then((result) => {
                if (result.isConfirmed) {
                    this.spinner.show();
                    this.apiUnit.deleteUnit(id).subscribe(() => {
                        this.spinner.hide();
                        this.utils.alertSwal({ title: 'Operación Exitosa', text: 'Se ha realizado la eliminacion de la unidad de forma exitosa', icon: 'success' });
                        this.onGridReady({ api: this.gridApi });
                    }, () => {
                        this.spinner.hide();
                        this.utils.alertSwal({ title: 'Error', text: 'Se ha presentado un error al intentar eliminar la unidad', icon: 'error' });
                    });
                }
            });
    }
}
